import { X } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";

type ConsentPageFrameProps = {
  /** Opens the exit confirmation. */
  onExit: () => void;
  /**
   * The content in the header row, opposite the close control. It fills the
   * space next to the cross. Thus the page starts with content.
   */
  eyebrow?: React.ReactNode;
  /** The body of the page. It scrolls when it is taller than the viewport. */
  children: React.ReactNode;
  /**
   * The agreement line. It stays at the bottom of the body. Thus it reads
   * directly above the rule and not below the button.
   */
  terms?: React.ReactNode;
  /** The action area. It stays at the bottom, in reach of the thumb. */
  footer: React.ReactNode;
};

/**
 * The frame of the consent page. It contains a close control, a body, and a
 * footer. Consent is one page. Thus this frame has no step counter and no
 * progress bar.
 */
export function ConsentPageFrame({
  onExit,
  eyebrow,
  children,
  terms,
  footer,
}: ConsentPageFrameProps) {
  return (
    <div className="flex min-h-dvh flex-1 flex-col">
      <div className="flex items-center justify-between gap-3 pl-6 pr-2 pt-5">
        {eyebrow ?? <span />}
        <Button
          variant="ghost"
          size="icon"
          className="size-9 shrink-0 text-muted-foreground"
          onClick={onExit}
        >
          <X className="size-5" />
          <span className="sr-only">Exit setup</span>
        </Button>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto px-6 pt-3 pb-6">
        {children}
        {/* mt-auto, so it sits on the rule rather than floating under the body. */}
        {terms ? <div className="mt-auto pt-6">{terms}</div> : null}
      </div>

      <div className="sticky bottom-0 border-t bg-background px-6 py-4">{footer}</div>
    </div>
  );
}
